import { c as _c } from "react/compiler-runtime";
import { useIntl } from '@onefootprint/hooks';
import { cx } from 'class-variance-authority';
import { useTranslation } from 'react-i18next';
import type { DeviceInsightEvent } from '@/components/entities/components/details/hooks/use-entity-device-insights';
import { useEntityContext } from '@/entity/hooks/use-entity-context';
import { displayForUserAgent, icoForUserAgent, isAppClip } from '@/utils/user-agent';
import useScrollIntoView from '../../hooks/use-scroll-into-view';
import getRegion from '../../utils/get-region';
import DetailsLine from '../details-line';
import EmployeeOnlyIcon from '../employee-only-icon';
import AboutAppClipAndInstantApp from './components/about-app-clip-and-instant-app';
const ROW_CLASS_NAME = 'grid w-full grid-cols-[auto_minmax(0,1fr)_auto] items-start gap-x-3 gap-y-0.5 rounded-default px-3 py-2 text-left';
type DeviceRowProps = {
  id: string;
  event: DeviceInsightEvent;
  isEmployeeOnly?: boolean;
  isSelected: boolean;
  /** Changes with each pick of the row's pin, which scrolls the list to it. */
  mapPick?: number;
  /** Has a pin on the map; a row without one has nothing to select. */
  isLocatable: boolean;
  onSelect: (id: string) => void;
};
const DeviceRow = t0 => {
  const $0 = _c(67);
  const {
    id,
    event,
    isSelected,
    mapPick,
    isLocatable,
    onSelect,
    isEmployeeOnly
  } = t0;
  let t1;
  if ($0[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "deviceInsights"
    };
    $0[0] = t1;
  } else {
    t1 = $0[0];
  }
  const {
    t
  } = useTranslation("entity-details", t1);
  const {
    formatDateWithTime
  } = useIntl();
  const context = useEntityContext();
  const ref = useScrollIntoView(mapPick);
  const {
    insight,
    kind,
    authEventKind
  } = event.event.data;
  const userAgent = insight.userAgent ?? "";
  const isPerson = context.kind === "person";
  let t2;
  if ($0[1] !== authEventKind || $0[2] !== isPerson || $0[3] !== kind || $0[4] !== t) {
    t2 = kind === "auth_event" ? isPerson && authEventKind && t($ => $.authEventKind[authEventKind]) : t($_0 => $_0.insightKind[kind]);
    $0[1] = authEventKind;
    $0[2] = isPerson;
    $0[3] = kind;
    $0[4] = t;
    $0[5] = t2;
  } else {
    t2 = $0[5];
  }
  const lead = t2;
  let t3;
  if ($0[6] !== insight.city || $0[7] !== insight.country || $0[8] !== insight.region || $0[9] !== isPerson) {
    t3 = isPerson ? [getRegion(insight.city, insight.region), insight.country].filter(Boolean).join(", ") : "";
    $0[6] = insight.city;
    $0[7] = insight.country;
    $0[8] = insight.region;
    $0[9] = isPerson;
    $0[10] = t3;
  } else {
    t3 = $0[10];
  }
  const place = t3;
  let t4;
  if ($0[11] !== insight.ipAddress || $0[12] !== lead || $0[13] !== place) {
    t4 = [lead, insight.ipAddress, place].filter(Boolean);
    $0[11] = insight.ipAddress;
    $0[12] = lead;
    $0[13] = place;
    $0[14] = t4;
  } else {
    t4 = $0[14];
  }
  const details = t4.join(" \xB7 ");
  let t5;
  if ($0[15] !== userAgent) {
    t5 = displayForUserAgent(userAgent);
    $0[15] = userAgent;
    $0[16] = t5;
  } else {
    t5 = $0[16];
  }
  const name = t5;
  let t6;
  if ($0[17] !== formatDateWithTime || $0[18] !== insight.timestamp) {
    t6 = formatDateWithTime(new Date(insight.timestamp), {
      month: "short",
      year: "numeric"
    });
    $0[17] = formatDateWithTime;
    $0[18] = insight.timestamp;
    $0[19] = t6;
  } else {
    t6 = $0[19];
  }
  const time = t6;
  const secondaryText = isSelected ? "text-secondary" : "text-tertiary";
  let t7;
  if ($0[20] !== userAgent) {
    t7 = icoForUserAgent(userAgent, "tertiary", "small");
    $0[20] = userAgent;
    $0[21] = t7;
  } else {
    t7 = $0[21];
  }
  let t8;
  if ($0[22] !== t7) {
    t8 = <span aria-hidden={true} className="row-span-2 mt-0.5 flex">{t7}</span>;
    $0[22] = t7;
    $0[23] = t8;
  } else {
    t8 = $0[23];
  }
  let t9;
  if ($0[24] !== isEmployeeOnly) {
    t9 = isEmployeeOnly && <span className="relative z-10 ml-1 inline-flex align-middle"><EmployeeOnlyIcon /></span>;
    $0[24] = isEmployeeOnly;
    $0[25] = t9;
  } else {
    t9 = $0[25];
  }
  let t10;
  if ($0[26] !== name || $0[27] !== t9) {
    t10 = <span className="truncate text-label-2 text-primary @max-md:col-span-2 @max-md:wrap-anywhere @max-md:whitespace-normal" data-dd-privacy="mask">{name}{t9}</span>;
    $0[26] = name;
    $0[27] = t9;
    $0[28] = t10;
  } else {
    t10 = $0[28];
  }
  let t11;
  if ($0[29] !== secondaryText) {
    t11 = cx("whitespace-nowrap text-body-3 @max-md:col-span-2 @max-md:col-start-2 @max-md:row-start-3", secondaryText);
    $0[29] = secondaryText;
    $0[30] = t11;
  } else {
    t11 = $0[30];
  }
  let t12;
  if ($0[31] !== t11 || $0[32] !== time) {
    t12 = <span className={t11}>{time}</span>;
    $0[31] = t11;
    $0[32] = time;
    $0[33] = t12;
  } else {
    t12 = $0[33];
  }
  let t13;
  if ($0[34] !== details || $0[35] !== insight.ipAddress || $0[36] !== lead || $0[37] !== place || $0[38] !== secondaryText) {
    t13 = details && <span className={cx("col-span-2 truncate text-body-3 @max-md:wrap-anywhere @max-md:whitespace-normal", secondaryText)} data-dd-privacy="mask"><DetailsLine lead={lead} ipAddress={insight.ipAddress} place={place} /></span>;
    $0[34] = details;
    $0[35] = insight.ipAddress;
    $0[36] = lead;
    $0[37] = place;
    $0[38] = secondaryText;
    $0[39] = t13;
  } else {
    t13 = $0[39];
  }
  let t14;
  if ($0[40] !== t10 || $0[41] !== t12 || $0[42] !== t13 || $0[43] !== t8) {
    t14 = <>{t8}{t10}{t12}{t13}</>;
    $0[40] = t10;
    $0[41] = t12;
    $0[42] = t13;
    $0[43] = t8;
    $0[44] = t14;
  } else {
    t14 = $0[44];
  }
  const content = t14;
  const t15 = isLocatable && (isSelected ? "bg-hover-control" : "hover:bg-hover");
  let t16;
  if ($0[45] !== t15) {
    t16 = cx("relative scroll-my-6 rounded-default transition-colors duration-100", t15);
    $0[45] = t15;
    $0[46] = t16;
  } else {
    t16 = $0[46];
  }
  let t17;
  if ($0[47] !== details || $0[48] !== id || $0[49] !== isLocatable || $0[50] !== isSelected || $0[51] !== name || $0[52] !== onSelect || $0[53] !== time) {
    t17 = isLocatable && <button type="button" aria-current={isSelected || undefined} aria-label={[name, time, details].join(" ")} onClick={() => onSelect(id)} className="absolute inset-0 cursor-pointer rounded-default" />;
    $0[47] = details;
    $0[48] = id;
    $0[49] = isLocatable;
    $0[50] = isSelected;
    $0[51] = name;
    $0[52] = onSelect;
    $0[53] = time;
    $0[54] = t17;
  } else {
    t17 = $0[54];
  }
  let t18;
  if ($0[55] !== content) {
    t18 = <div className={ROW_CLASS_NAME}>{content}</div>;
    $0[55] = content;
    $0[56] = t18;
  } else {
    t18 = $0[56];
  }
  let t19;
  if ($0[57] !== secondaryText || $0[58] !== t || $0[59] !== userAgent) {
    t19 = isAppClip(userAgent) && <p className={cx("flex items-center gap-1 pr-3 pb-2 pl-10 text-body-3", secondaryText)}>{t(_temp)}<span className="relative z-10 flex"><AboutAppClipAndInstantApp kind="appClip" /></span></p>;
    $0[57] = secondaryText;
    $0[58] = t;
    $0[59] = userAgent;
    $0[60] = t19;
  } else {
    t19 = $0[60];
  }
  let t20;
  if ($0[61] !== ref || $0[62] !== t16 || $0[63] !== t17 || $0[64] !== t18 || $0[65] !== t19) {
    t20 = <li ref={ref} className={t16}>{t17}{t18}{t19}</li>;
    $0[61] = ref;
    $0[62] = t16;
    $0[63] = t17;
    $0[64] = t18;
    $0[65] = t19;
    $0[66] = t20;
  } else {
    t20 = $0[66];
  }
  return t20;
};
export default DeviceRow;
function _temp($_1) {
  return $_1.appClip.label;
}